import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

// ── Types ────────────────────────────────────────────────────────────────────

interface PLResponse {
  period: string;
  gross_revenue_agorot: number;
  total_expenses_agorot: number;
  net_profit_agorot: number;
  vat_collected_agorot: number;
  withholding_tax_agorot: number;
  generated_at: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

export function fmtIls(agorot: number): string { return '₪' + (agorot / 100).toFixed(2); }
export function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// ── Sub-components ────────────────────────────────────────────────────────────

function PLRow({ label, value, highlight, positive }: { label: string; value: string; highlight?: boolean; positive?: boolean }) {
  const color = positive === true ? '#34d399' : positive === false ? '#f87171' : 'var(--omega-ink)';
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '1fr 160px', gap: 8,
      padding: '10px 16px',
      borderBottom: '1px solid rgba(255,255,255,0.05)',
      background: highlight ? 'rgba(125,211,252,0.06)' : 'transparent',
    }}>
      <span style={{ fontSize: '9pt', color: 'var(--omega-ink)', fontWeight: highlight ? 700 : 400 }}>{label}</span>
      <span style={{ fontSize: '9pt', fontFamily: 'var(--omega-mono)', textAlign: 'right', color, fontWeight: highlight ? 700 : 400 }}>
        {value}
      </span>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function FinancePLPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));

  const inputStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid var(--omega-rim, rgba(255,255,255,0.12))',
    borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)',
    fontSize: '8.5pt', padding: '4px 8px', outline: 'none',
  };

  const { data, isPending, isError } = useQuery({
    queryKey: ['finance', 'pl', period],
    queryFn: () => apiFetch<PLResponse>(`/finance/pl?period=${period}`),
    refetchInterval: 30_000,
  });

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA', to: '/omega' },
        { label: 'Finance', to: '/omega/finance' },
        { label: 'CPA — P&L' },
      ]} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, marginTop: 8, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          P&L — Profit and loss
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
          <label style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)' }}>Period:</label>
          <input
            type="text"
            value={period}
            onChange={e => setPeriod(e.target.value)}
            placeholder="YYYY-MM"
            style={inputStyle}
          />
        </div>
      </div>

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError && <div style={{ color: '#f87171', fontSize: '9pt' }}>Error loading data.</div>}

      {data && (
        <div style={{
          background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 6, overflow: 'hidden', maxWidth: 560,
        }}>
          <PLRow label="Gross revenue" value={fmtIls(data.gross_revenue_agorot)} positive />
          <PLRow label="Total expenses" value={`(${fmtIls(data.total_expenses_agorot)})`} positive={false} />
          <PLRow label="VAT collected" value={fmtIls(data.vat_collected_agorot)} />
          <PLRow label="Withholding tax" value={`(${fmtIls(data.withholding_tax_agorot)})`} />
          <PLRow
            label="Net profit"
            value={fmtIls(data.net_profit_agorot)}
            highlight
            positive={data.net_profit_agorot >= 0}
          />
          <div style={{ padding: '6px 16px', fontSize: '7.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)' }}>
            Created: {fmtDate(data.generated_at)}
          </div>
        </div>
      )}
    </div>
  );
}
